import Table, { ColumnsType } from "antd/lib/table";
import update from 'immutability-helper';
import { BaseEntity } from "models/common";
import { HTMLAttributes, useCallback, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

interface SortableTableProps<T extends BaseEntity> {
    data: Array<T>;
    columns: ColumnsType<T>;
    onReorder: (items: Array<T>) => Promise<void>;
    isLoading: boolean;
}

interface DraggableBodyRowProps extends HTMLAttributes<HTMLTableRowElement> {
    index: number;
    moveRow: (dragIndex: number, hoverIndex: number) => void;
}

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({
    index,
    moveRow,
    className,
    style,
    ...restProps
}: DraggableBodyRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);
    const [{ isOver, dropClassName }, drop] = useDrop({
        accept: type,
        collect: monitor => {
            const { index: dragIndex } = monitor.getItem() || {};
            if (dragIndex === index) {
                return {};
            }
            return {
                isOver: monitor.isOver(),
                dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
            };
        },
        drop: (item: { index: number }) => {
            moveRow(item.index, index);
        },
    });
    const [, drag] = useDrag({
        type,
        item: { index },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });
    drop(drag(ref));

    return (
        <tr
            ref={ref}
            className={`${className}${isOver ? dropClassName : ''}`}
            style={{ cursor: 'move', ...style }}
            {...restProps}
        />
    );
};

const SortableTable = <T extends BaseEntity>({
    data, columns, onReorder, isLoading }: SortableTableProps<T>
) => {
    const moveRow = useCallback(
        async (dragIndex: number, hoverIndex: number) => {
            const dragRow = data[dragIndex];
            const reorderedData = update(data, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, dragRow],
                ],
            });
            await onReorder(reorderedData);
        },
        [data, onReorder],
    );

    return <DndProvider backend={HTML5Backend}>
        <Table
            pagination={false}
            dataSource={data}
            loading={isLoading}
            columns={columns}
            rowKey={r => r.id}
            components={{ body: { row: DraggableBodyRow } }}
            onRow={(_, index) => {
                const attr = {
                    index,
                    moveRow,
                };
                return attr as HTMLAttributes<any>;
            }}
            size="small"
        />
    </DndProvider>
};

export default SortableTable;